/**
 * useNotificationNavigation - Weiche In-App-Navigation bei Notification-Klick
 *
 * Der Service Worker (push-sw.js) sendet beim Notification-Klick eine Message an
 * das fokussierte Fenster. Hier wird sie in eine vue-router-Navigation übersetzt
 * — ohne Full-Reload. Nur In-App-Ziele (gleiche Origin) werden zugelassen.
 *
 * Die Nachricht wird über den mitgeschickten Port BESTÄTIGT. Bleibt die Bestätigung
 * aus, navigiert der Service Worker nach kurzer Wartezeit hart. Hintergrund: iOS
 * beendet installierte PWAs aggressiv; beim Antippen der Benachrichtigung startet
 * die Seite unter Umständen erst, während der Service Worker schon sendet.
 */
import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

interface NotificationNavigateMessage {
  type: 'notification-navigate'
  url: string
}

function isNavigateMessage(data: unknown): data is NotificationNavigateMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'notification-navigate' &&
    typeof (data as { url?: unknown }).url === 'string'
  )
}

/**
 * Zuletzt empfangenes Ziel, das noch nicht ausgeführt werden konnte.
 *
 * Der Listener hängt an `onMounted`. Trifft die Nachricht davor ein — beim
 * Kaltstart der PWA durchaus möglich —, gäbe es sonst niemanden, der sie
 * entgegennimmt. Deshalb wird auf Modulebene ein früher Listener registriert,
 * der Ziel UND Antwortkanal zwischenspeichert; die Komponente arbeitet beides
 * beim Einhängen ab.
 */
let pendingTarget: { url: string; port: MessagePort | undefined } | null = null

function rememberEarlyMessage(event: MessageEvent): void {
  if (isNavigateMessage(event.data)) {
    pendingTarget = { url: event.data.url, port: event.ports?.[0] }
    // KEINE Bestätigung an dieser Stelle — siehe acknowledge().
  }
}

/**
 * Dem Service Worker melden, dass die Navigation stattgefunden hat.
 *
 * Entscheidend ist der Zeitpunkt: erst NACH der Navigation, nie beim Empfang.
 * Die erste Fassung bestätigte sofort — der Service Worker las das als „erledigt"
 * und übersprang seinen harten Rückfall. Traf die Nachricht ein, während die App
 * noch startete, war damit beides weg: Die Komponente hatte noch nicht navigiert,
 * und das Sicherheitsnetz war bereits abgeschaltet. Die App kam in den
 * Vordergrund und blieb stehen — genau das war am 16.08.2026 auf dem Gerät zu
 * sehen.
 *
 * Die Bestätigung heißt also „navigiert", nicht „angekommen".
 */
function acknowledge(port: MessagePort | undefined): void {
  if (!port) return
  try {
    port.postMessage({ type: 'notification-navigate-ack' })
  } catch {
    // Port bereits geschlossen — dann navigiert der Service Worker selbst.
  }
}

// Sofort beim Laden des Moduls greifen, nicht erst beim Einhängen der Komponente.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', rememberEarlyMessage)
}

export function useNotificationNavigation() {
  const router = useRouter()

  /**
   * Führt die Navigation aus und meldet danach Vollzug.
   *
   * Gibt `true` zurück, wenn das Ziel angesteuert wurde oder bereits offen war.
   * Bei einem fremden oder ungültigen Ziel wird NICHT bestätigt — dann soll der
   * Service Worker weiterhin selbst übernehmen dürfen.
   */
  function navigateAndAcknowledge(rawUrl: string, port: MessagePort | undefined): boolean {
    try {
      const target = new URL(rawUrl, window.location.origin)
      if (target.origin !== window.location.origin) return false // nur In-App-Ziele
      const path = target.pathname + target.search + target.hash

      if (router.currentRoute.value.fullPath === path) {
        acknowledge(port) // schon am Ziel, nichts zu tun
        return true
      }

      // Erst wenn `router.push` durch ist, ist die Navigation wirklich geschehen.
      // Der Aufruf ist asynchron; würde direkt danach bestätigt, hieße die
      // Bestätigung wieder nur „empfangen" — genau der Fehler, der auf dem Gerät
      // dazu führte, dass die App stehenblieb. Schlägt die Navigation fehl (etwa
      // durch einen Router-Guard), wird NICHT bestätigt, und der Service Worker
      // übernimmt.
      void router.push(path).then(
        () => acknowledge(port),
        () => {
          /* Navigation abgebrochen — keine Bestätigung, Rückfall greift */
        }
      )
      return true
    } catch {
      return false // ungültige URL -> ignorieren, Service Worker übernimmt
    }
  }

  function handleMessage(event: MessageEvent) {
    if (!isNavigateMessage(event.data)) return
    navigateAndAcknowledge(event.data.url, event.ports?.[0])
  }

  onMounted(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage)
    }
    // Nachricht, die vor dem Einhängen eintraf, nachholen — samt Bestätigung.
    if (pendingTarget) {
      const { url, port } = pendingTarget
      pendingTarget = null
      navigateAndAcknowledge(url, port)
    }
  })

  onUnmounted(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', handleMessage)
    }
  })
}
